<template>
  <div>
    <b-jumbotron>
      <b-container>
        <b-alert show dismissible v-if="alert"> Saved </b-alert>
        <div class="text-center" v-if="loading"><b-spinner></b-spinner></div>
        <b-row class="mb-5" v-else>
          <div class="col">
            <h1>Translation Manager</h1>
          </div>
          <b-col class="text-right">
            <b-button variant="primary" class="mr-1" @click="save">
              Save Changes
            </b-button>
            <b-button variant="light" @click="exportCSV">
              Download CSV
            </b-button>
          </b-col>
        </b-row>
        <hr />

        <b-row class="mt-5">
          <b-col>
            <h5>Language</h5>
            <b-form-select v-model="selectedLanguage">
              <b-form-select-option
                v-for="language in languages"
                :key="language.english_name"
                :value="language.code"
              >
                {{ language.english_name }}
              </b-form-select-option>
            </b-form-select>
          </b-col>
          <b-col>
            <h5>Word Set</h5>
            <b-form-select v-model="selectedWordSet">
              <b-form-select-option
                v-for="(wordset, index) in wordsets"
                :key="index"
                :value="wordset"
              >
                {{ wordset.name }}
              </b-form-select-option>
            </b-form-select>
          </b-col>
        </b-row>
      </b-container>
    </b-jumbotron>
    <b-container v-if="!selectedWordSet" class="mt-5">
      Please choose a word set above
    </b-container>
    <b-container v-if="selectedWordSet && items" class="mt-5">
      <b-row>
        <b-col md="4">
          <b-input-group class="mb-3">
            <b-input-group-prepend>
              <b-input-group-text><i class="fa">&#xf002;</i></b-input-group-text>
            </b-input-group-prepend>
            <b-form-input
              v-model="query"
              @input="onQuery"
              type="text"
              class="form-control"
              placeholder="Search..."
            ></b-form-input>
          </b-input-group>
          <div class="form-check">
            <label class="form-check-label">
              <input v-model="caseSensitiveSearch" class="form-check-input" type="checkbox" />
              Case sensitive
            </label>
          </div>
        </b-col>
        <b-col md="4">
          <b-form-group>
            <label>Labels with variables</label>
            <b-form-select v-model="variableStatus" required>
              <b-form-select-option value="">
                No filtering
              </b-form-select-option>
              <b-form-select-option value="show">
                Show labels with variables
              </b-form-select-option>
              <b-form-select-option value="hide">
                Hide labels with variables
              </b-form-select-option>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-button block variant="light" @click="autoTranslate()">
            Auto Translate
          </b-button>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="12">
          <b-card-group columns>
            <b-card v-for="item in filteredItems" :key="item.id" no-body>
              <b-card-header>Item: #{{ item.id }} {{ item.key }} </b-card-header>
              <b-card-body>
                <b-form-group
                  v-for="(field, index) in selectedWordSet.fields"
                  :key="index"
                  class="form-group"
                >
                  <label style="font-weight: bold; text-transform: uppercase">{{ field }}</label>
                  <small class="form-text text-muted">Default: {{ item[field] }}</small>
                  <b-form-input
                    v-model="item[field + '_local']"
                    class="form-control"
                  ></b-form-input>
                </b-form-group>
              </b-card-body>
            </b-card>
          </b-card-group>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import $api from "@/services/api";
import $googletranslate from "@/services/googletranslate";
import $csv from "@/services/csv";

import wordsetconfig from "./wordsetconfig";

export default {
  data() {
    return {
      languages: null,
      wordsets: wordsetconfig,
      selectedLanguage: "en",
      selectedWordSet: null,
      query: null,
      items: null,
      loading: false,
      alert: false,
      caseSensitiveSearch: false,
      variableStatus: ""
    };
  },

  computed: {
    filteredItems() {
      let results = this.items;
      if (results && this.variableStatus) {
        results = results.filter(item => {
          if (this.variableStatus === "show")
            return item.value.includes("{") && item.value.includes("}");
          if (this.variableStatus === "hide")
            return !item.value.includes("{") && !item.value.includes("}");
        });
      }
      if (results && this.query) {
        results = results.filter(item => {
          const itemString = this.caseSensitiveSearch
            ? JSON.stringify(item)
            : JSON.stringify(item).toLowerCase();
          const queryString = this.caseSensitiveSearch ? this.query : this.query.toLowerCase();
          return itemString.includes(queryString);
        });
      }
      return results;
    }
  },

  watch: {
    selectedLanguage() {
      this.loadItems();
      this.alert = false;
    },

    selectedWordSet() {
      this.loadItems();
      this.alert = false;
    }
  },

  created() {
    this.refreshData();
  },

  methods: {
    refreshData() {
      $api.get("/v1/common/languages").then(languages => {
        this.languages = languages;
      });
    },

    loadItems() {
      if (!this.selectedWordSet || !this.selectedLanguage) {
        return false;
      }
      $api
        .get(this.selectedWordSet.endpoint, {
          _language: this.selectedLanguage
        })
        .then(items => {
          this.items = items;
        });
      return this.items;
    },

    autoTranslate() {
      this.alert = false;
      this.filteredItems.forEach(item => {
        this.selectedWordSet.fields.forEach(field => {
          if (!item[`${field}_local`]) {
            // Only proceed if there is no existing local translation
            $googletranslate.translate(item[field], this.selectedLanguage).then(translatedValue => {
              /* eslint-disable */
              item[`${field}_local`] = translatedValue;
            });
          }
        });
      });
    },

    save() {
      const payload = this.items.map(item => {
        const newItem = {};
        newItem.item_id = item.id;
        newItem.language = this.selectedLanguage;

        this.selectedWordSet.fields.forEach(field => {
          newItem[field] = item[`${field}_local`];
        });

        return newItem;
      });
      if (this.selectedWordSet.table === "applabels_translation") {
        const tooLongItems = payload.filter(item => {
          return item.value && item.value.length > 200;
        });
        if (tooLongItems.length) {
          const tooLongTexts = tooLongItems.map(item => {
            return `Item #${item.item_id}: ${item.value.length} chars`;
          });
          alert(
            `Sorry, there is a character limit of 200 per item.  The following are too long: ${tooLongTexts.join(
              ","
            )}`
          );
          return;
        }
      }

      this.loading = true;
      $api
        .post("/v1/internal/translation", {
          wordset: this.selectedWordSet.table,
          items: payload
        })
        .then(() => {
          this.loading = false;
          this.alert = true;
        });
      this.alert = false;
    },

    exportCSV() {
      this.alert = false;
      // Downloads all word sets and exports as single CSV
      const rows = [];

      return Promise.all(
        Object.values(this.wordsets).map(wordset =>
          $api.get(wordset.endpoint, {
            _language: this.selectedLanguage
          })
        )
      )
        .then(resps => {
          resps.forEach((resp, r) => {
            const wordset = Object.values(this.wordsets)[r];
            resp.forEach(item => {
              wordset.fields.forEach(field => {
                rows.push({
                  collection: wordset.name,
                  id: item.id,
                  field,
                  default: item[field],
                  value: item[`${field}_local`]
                });
              });
            });
          });
        })
        .then(() => {
          $csv.export(rows, ["collection", "id", "field", "default", "value"], "translation.csv");
        });
    },

    onQuery() {
      this.alert = false;
    }
  }
};
</script>
